/**
 * The compliance screens.
 *
 * Six of the seven are lists over a single endpoint, so they live together
 * rather than in six near-identical files. The overview is the exception and
 * has its own file, because it reads across all of them.
 *
 * The recurring judgement here is that a compliance date is not just a date:
 * an overdue filing is a different kind of fact from an upcoming one, and the
 * screens say which rather than printing an ISO string and leaving the reader
 * to work out today's date.
 */
import { ResourceListPage } from "@/components/ResourceListPage";
import { Badge, StatusBadge } from "@/components/ui";
import { formatDate, formatDateTime } from "@/lib/money";
import { daysUntil } from "./types";
import type {
  AuditFileRow,
  DataSubjectRequestRow,
  EInvoiceRow,
  ObligationRow,
  RegulatoryReportRow,
  RetentionPolicyRow,
} from "./types";

/** Due-date cell that distinguishes overdue from upcoming. */
export function DueCell({ due }: { due: string }) {
  const days = daysUntil(due);
  if (days < 0) {
    return (
      <span>
        {formatDate(due)} <Badge tone="danger">{Math.abs(days)}d overdue</Badge>
      </span>
    );
  }
  if (days <= 7) {
    return (
      <span>
        {formatDate(due)} <Badge tone="warning">in {days}d</Badge>
      </span>
    );
  }
  return <span>{formatDate(due)}</span>;
}

export function ComplianceCalendarPage() {
  return (
    <ResourceListPage<ObligationRow>
      title="Compliance calendar"
      subtitle="Statutory obligations and when they fall due. Overdue items are called out rather than sorted away."
      breadcrumb={["Compliance", "Calendar"]}
      path="/compliance/calendar"
      queryKey={["compliance", "calendar"]}
      emptyTitle="No obligations recorded"
      emptyMessage="Obligations come from the installed country pack — VAT returns, withholding filings, statutory accounts. A workspace with no pack installed has none."
      columns={[
        { key: "title", label: "Obligation" },
        { key: "obligation_type", label: "Type" },
        {
          key: "due_date",
          label: "Due",
          render: (r) => <DueCell due={r.due_date} />,
        },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
      ]}
    />
  );
}

export function RegulatoryReportsPage() {
  return (
    <ResourceListPage<RegulatoryReportRow>
      title="Regulatory reports"
      subtitle="Filings prepared for a tax authority or regulator, by period."
      breadcrumb={["Compliance", "Regulatory reports"]}
      path="/compliance/regulatory-reports"
      queryKey={["compliance", "regulatory-reports"]}
      createLabel="Prepare report"
      emptyTitle="No reports prepared"
      emptyMessage="A regulatory report is generated for a period and a jurisdiction, then filed. Preparing one does not file it."
      createFields={[
        {
          field: "reportType",
          label: "Report type",
          required: true,
          options: [
            "VAT Return",
            "Withholding Tax",
            "Sales Tax",
            "Intrastat",
            "EC Sales List",
            "Statutory Accounts",
          ],
        },
        {
          field: "period",
          label: "Period",
          required: true,
          placeholder: "2026-Q1",
          hint: "The period the filing covers.",
        },
        {
          field: "countryCode",
          label: "Country",
          placeholder: "DE",
          hint: "ISO country code of the filing jurisdiction.",
        },
      ]}
      columns={[
        { key: "report_type", label: "Report" },
        {
          key: "period",
          label: "Period",
          render: (r) => <span className="atlas-mono">{r.period}</span>,
        },
        {
          key: "country_code",
          label: "Jurisdiction",
          render: (r) => r.country_code ?? "—",
        },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
        {
          key: "created_at",
          label: "Prepared",
          render: (r) => formatDate(r.created_at),
        },
      ]}
    />
  );
}

export function EInvoicingPage() {
  return (
    <ResourceListPage<EInvoiceRow>
      title="E-invoicing"
      subtitle="Invoices submitted to a tax authority under a country e-invoicing mandate."
      breadcrumb={["Compliance", "E-invoicing"]}
      path="/compliance/e-invoicing"
      queryKey={["compliance", "e-invoicing"]}
      createLabel="Submit invoice"
      emptyTitle="Nothing submitted"
      emptyMessage="Where a jurisdiction mandates e-invoicing, each invoice is signed and submitted to the authority. The country pack declares whether a mandate applies."
      createFields={[
        {
          field: "invoiceId",
          label: "Invoice ID",
          required: true,
          hint: "The sales or supplier invoice being submitted.",
        },
        {
          field: "invoiceType",
          label: "Invoice type",
          options: ["SalesInvoice", "SupplierInvoice", "CreditNote"],
        },
        {
          field: "countryCode",
          label: "Country",
          required: true,
          placeholder: "SA",
        },
      ]}
      columns={[
        {
          key: "invoice_id",
          label: "Invoice",
          render: (r) => (
            <span className="atlas-mono">
              {r.invoice_id ? r.invoice_id.slice(0, 8) : "—"}
            </span>
          ),
        },
        { key: "invoice_type", label: "Type" },
        {
          key: "country_code",
          label: "Jurisdiction",
          render: (r) => r.country_code ?? "—",
        },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
        {
          key: "created_at",
          label: "Submitted",
          render: (r) => formatDateTime(r.created_at),
        },
      ]}
    />
  );
}

export function AuditFilesPage() {
  return (
    <ResourceListPage<AuditFileRow>
      title="Audit files"
      subtitle="Machine-readable ledger extracts an auditor or tax authority can request — SAF-T and its equivalents."
      breadcrumb={["Compliance", "Audit files"]}
      path="/compliance/audit-files"
      queryKey={["compliance", "audit-files"]}
      createLabel="Generate audit file"
      emptyTitle="No audit files"
      emptyMessage="An audit file is a full ledger extract in a prescribed format. Generating one is a read of the ledger and changes nothing."
      createFields={[
        {
          field: "fileType",
          label: "Format",
          required: true,
          options: ["SAF-T", "GDPdU", "FEC", "Audit File XML", "CSV Extract"],
        },
        {
          field: "period",
          label: "Period",
          required: true,
          placeholder: "2026",
          hint: "Year or period the extract covers.",
        },
      ]}
      columns={[
        { key: "file_type", label: "Format" },
        {
          key: "period",
          label: "Period",
          render: (r) => <span className="atlas-mono">{r.period}</span>,
        },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
        {
          key: "object_key",
          label: "File",
          // A generated file that never produced an object is a failed
          // generation, so it says so rather than showing an empty cell.
          render: (r) =>
            r.object_key ? (
              "Ready"
            ) : (
              <span className="atlas-muted">Not generated</span>
            ),
        },
        {
          key: "created_at",
          label: "Requested",
          render: (r) => formatDateTime(r.created_at),
        },
      ]}
    />
  );
}

export function DataSubjectRequestsPage() {
  return (
    <ResourceListPage<DataSubjectRequestRow>
      title="Data subject requests"
      subtitle="Access, export and erasure requests from individuals, with the clock that applies to each."
      breadcrumb={["Compliance", "Data subject requests"]}
      path="/compliance/data-subject-requests"
      queryKey={["compliance", "data-subject-requests"]}
      createLabel="Log request"
      emptyTitle="No requests"
      emptyMessage="Under GDPR and comparable regimes an individual can ask what you hold about them, ask for a copy, or ask you to erase it. Each carries a statutory deadline from the date it was received."
      createFields={[
        {
          field: "requestType",
          label: "Request type",
          required: true,
          options: [
            "Access",
            "Export",
            "Erasure",
            "Rectification",
            "Restriction",
          ],
        },
        {
          field: "subjectEmail",
          label: "Subject email",
          required: true,
          type: "email",
          hint: "Identifies the individual whose data is in scope.",
        },
      ]}
      columns={[
        { key: "request_type", label: "Type" },
        { key: "subject_email", label: "Subject" },
        {
          key: "status",
          label: "Status",
          render: (r) => <StatusBadge status={r.status} />,
        },
        {
          key: "requested_at",
          label: "Received",
          render: (r) => formatDate(r.requested_at),
        },
        {
          key: "deadline",
          label: "Respond by",
          // One calendar month is the GDPR default; jurisdictions differ, so
          // this is shown as guidance rather than as an authoritative date.
          render: (r) => {
            const due = new Date(r.requested_at);
            due.setMonth(due.getMonth() + 1);
            return <DueCell due={due.toISOString()} />;
          },
        },
      ]}
    />
  );
}

export function RetentionPoliciesPage() {
  return (
    <ResourceListPage<RetentionPolicyRow>
      title="Retention policies"
      subtitle="How long each kind of record is kept, and what happens when it expires."
      breadcrumb={["Compliance", "Retention"]}
      path="/compliance/retention-policies"
      queryKey={["compliance", "retention-policies"]}
      createLabel="New policy"
      emptyTitle="No retention policies"
      emptyMessage="Without a policy nothing is ever aged out. Ledger and audit records are append-only regardless: a retention policy governs archival, never deletion of a posted entry."
      createFields={[
        {
          field: "entityType",
          label: "Record type",
          required: true,
          placeholder: "AuditLogEntry",
        },
        {
          field: "retentionDays",
          label: "Retain for (days)",
          required: true,
          type: "number",
          placeholder: "2555",
        },
        {
          field: "action",
          label: "On expiry",
          options: ["Archive", "Anonymize", "Delete"],
        },
      ]}
      toBody={(form) => ({
        entityType: form.entityType,
        // The API type-checks this as a number, not a string.
        retentionDays: Number(form.retentionDays),
        action: form.action || "Archive",
      })}
      columns={[
        { key: "entity_type", label: "Record type" },
        {
          key: "retention_days",
          label: "Retention",
          numeric: true,
          render: (r) =>
            `${r.retention_days} days (~${Math.round(r.retention_days / 365)}y)`,
        },
        { key: "action", label: "On expiry" },
        {
          key: "is_active",
          label: "Active",
          render: (r) =>
            r.is_active ? (
              <Badge tone="success">Active</Badge>
            ) : (
              <Badge>Inactive</Badge>
            ),
        },
      ]}
    />
  );
}
